// The pyramid — levels, the two ordering invariants, group move, and the outline rows
// (TODO §18.0–§18.2).
//
// **Phase 1, plus one piece of phase 2a.** This file is structure, the slide list, and — since
// presentation asked for it — §18.3.1/§18.3.2's `pyramidStep` at the bottom. It is still not
// links: `deck:slide/…` hrefs, the return stack (§18.3.3) and the Links taskpane belong to a
// later sprint, and nothing here is shaped to anticipate them.
//
// §18.1.2 The deck stays ONE flat ordered array. A level is a tag on the slide, parentage is
// derived by adjacency — an evidence slide belongs to the nearest preceding storyline slide —
// and every consumer that reads `slides` in order (Slide, PresentationView, PrintView,
// htmlExport, slideThumbnails, snapshotRegistry, deckBundle, the nav) keeps reading it in
// order and gets every slide. A nested `children[]` would have changed all of them for a
// hierarchy that is only ever displayed flat anyway.
//
// §18.0 Naming: `nugget` in this codebase means §17's shared-slide object and only that.
// The pyramid's vocabulary is `level` / `storyline` / `evidence` / `backup`.

import type { SlideData, SlideLevel } from "@/types/document";

// §18.1.4 Three levels now, more later. Every comparison below goes through `depth()` and
// these constants rather than the string literals, so a fourth level is one row in this map
// plus its label — not a grep across the outline, the reducers and the numbering helper.
const LEVEL_DEPTH: Record<SlideLevel, 0 | 1 | 2> = {
  storyline: 0,
  evidence: 1,
  backup: 2,
};

export const DEPTH_STORYLINE = 0;
export const DEPTH_EVIDENCE = 1;
export const DEPTH_BACKUP = 2;

// §18.1.1 Absent reads as `storyline`, so every deck already in storage is a valid one-level
// pyramid and no consumer branches on the field being missing. Resolved HERE, once, rather
// than as `slide.level ?? "storyline"` at fifteen call sites.
export function levelOf(slide: Pick<SlideData, "level">): SlideLevel {
  return slide.level ?? "storyline";
}

export function depth(level: SlideLevel): 0 | 1 | 2 {
  return LEVEL_DEPTH[level];
}

export function depthOf(slide: Pick<SlideData, "level">): 0 | 1 | 2 {
  return depth(levelOf(slide));
}

// §18.2.12 The pyramid principle's own advice, as a hint and never as a block: a storyline
// that runs past ~10 slides has stopped being a storyline, and a claim needing more than ~5
// pieces of evidence is usually two claims. A deck that needs twelve is allowed twelve.
export const STORYLINE_SOFT_MAX = 10;
export const EVIDENCE_SOFT_MAX = 5;

// === Active slots ===
//
// Archived slides live in the same array and are filtered out for display, so every rule
// below is stated over the ACTIVE subsequence. Reordering writes the new active order back
// into the slots the active slides already occupied, leaving archived rows exactly where
// they are — which is what `moveSlide` has always done by swapping past them.

function activeSlots(slides: SlideData[]): number[] {
  const slots: number[] = [];
  for (let i = 0; i < slides.length; i++)
    if (!slides[i].archived) slots.push(i);
  return slots;
}

function writeActiveOrder(
  slides: SlideData[],
  slots: number[],
  nextActive: SlideData[],
): SlideData[] {
  const out = slides.slice();
  for (let k = 0; k < slots.length; k++) out[slots[k]] = nextActive[k];
  return out;
}

/**
 * §18.1.3 The two ordering invariants, in the one function that states them:
 *
 * **(a)** an `evidence` slide is always preceded (ignoring archived slides) by a `storyline`
 * slide, so the first active slide is always `storyline`; **(b)** `backup` slides form a
 * contiguous **suffix** — once the first backup appears, everything after it is backup.
 *
 * Both hold at all times, so no consumer ever has to handle an evidence slide with no parent
 * or a storyline slide stranded behind the backup divider. Called by every mutation that can
 * violate them (set level, insert, move, archive, restore, purge), by the migration, and by
 * the import path.
 *
 * The repairs are the minimal ones. (b) is a **stable partition** — the backups move to the
 * end and everything keeps its relative order — because the level is an editorial claim
 * (§18.1.2) and the position is not, so position is the cheaper thing to correct. (a) promotes
 * the first active slide when it is not already storyline, which is the only place (a) can
 * break once (b) holds.
 *
 * **Levels are repaired in place**, so this is safe on an immer draft and must only be called
 * on slides the caller owns. The returned array is new; slide identity is preserved.
 */
export function normalizePyramid(slides: SlideData[]): SlideData[] {
  const slots = activeSlots(slides);
  if (slots.length === 0) return slides;
  const active = slots.map((i) => slides[i]);

  // (b) backups to the end, stably.
  const main = active.filter((s) => depthOf(s) !== DEPTH_BACKUP);
  const next =
    main.length === active.length
      ? active
      : [...main, ...active.filter((s) => depthOf(s) === DEPTH_BACKUP)];

  // (a) the deck's first active slide carries the first claim.
  if (depthOf(next[0]) !== DEPTH_STORYLINE) delete next[0].level;

  return next === active ? slides : writeActiveOrder(slides, slots, next);
}

// === Groups ===
//
// Given the invariants, the active list is always `G1 … Gn` followed by the backup suffix,
// where each `Gi` is one storyline slide and the evidence slides that follow it. Everything
// below builds that view, changes it, and flattens it back.

interface Group {
  head: SlideData;
  evidence: SlideData[];
}

function buildGroups(active: SlideData[]): {
  groups: Group[];
  backups: SlideData[];
} {
  const groups: Group[] = [];
  const backups: SlideData[] = [];
  for (const slide of active) {
    const d = depthOf(slide);
    if (d === DEPTH_BACKUP) backups.push(slide);
    // An evidence slide with nothing in front of it cannot happen once the invariants hold;
    // treating it as a head rather than dropping it keeps this total for any input.
    else if (d === DEPTH_EVIDENCE && groups.length > 0)
      groups[groups.length - 1].evidence.push(slide);
    else groups.push({ head: slide, evidence: [] });
  }
  return { groups, backups };
}

function flattenGroups(groups: Group[], backups: SlideData[]): SlideData[] {
  const out: SlideData[] = [];
  for (const g of groups) {
    out.push(g.head);
    out.push(...g.evidence);
  }
  out.push(...backups);
  return out;
}

/**
 * §18.2.3 Move the **group**, not the slide — the one behavioural change in phase 1 that can
 * surprise, and the one that has to exist: without it, moving a storyline slide leaves its
 * evidence behind, silently re-parented to whatever slide it landed behind.
 *
 * - A **storyline** slide moves together with its evidence block, past the whole of the
 *   previous/next group. It cannot cross into the backup suffix.
 * - An **evidence** slide moves within its parent's block and, at the block edge, **re-parents**
 *   to the adjacent storyline slide — what dragging visibly does in every outliner. In the flat
 *   list that is still a one-position move, so the button feels like the button it was.
 * - A **backup** slide moves freely inside the backup suffix and cannot leave it.
 *
 * Returns the input array **by reference** when the move is not available; the outline's
 * `canMoveUp`/`canMoveDown` state the same rules for the buttons.
 */
export function movePyramid(
  slides: SlideData[],
  id: string,
  direction: "up" | "down",
): SlideData[] {
  const slots = activeSlots(slides);
  const active = slots.map((i) => slides[i]);
  const { groups, backups } = buildGroups(active);
  const up = direction === "up";
  const done = () =>
    writeActiveOrder(slides, slots, flattenGroups(groups, backups));

  const bi = backups.findIndex((s) => s.id === id);
  if (bi !== -1) {
    const to = up ? bi - 1 : bi + 1;
    if (to < 0 || to >= backups.length) return slides;
    [backups[bi], backups[to]] = [backups[to], backups[bi]];
    return done();
  }

  const gi = groups.findIndex((g) => g.head.id === id);
  if (gi !== -1) {
    const to = up ? gi - 1 : gi + 1;
    if (to < 0 || to >= groups.length) return slides;
    [groups[gi], groups[to]] = [groups[to], groups[gi]];
    return done();
  }

  for (let g = 0; g < groups.length; g++) {
    const ev = groups[g].evidence;
    const ei = ev.findIndex((s) => s.id === id);
    if (ei === -1) continue;
    if (up) {
      if (ei > 0) [ev[ei], ev[ei - 1]] = [ev[ei - 1], ev[ei]];
      else if (g === 0) return slides;
      else groups[g - 1].evidence.push(ev.splice(ei, 1)[0]);
    } else if (ei < ev.length - 1) {
      [ev[ei], ev[ei + 1]] = [ev[ei + 1], ev[ei]];
    } else if (g === groups.length - 1) {
      return slides;
    } else {
      groups[g + 1].evidence.unshift(ev.splice(ei, 1)[0]);
    }
    return done();
  }

  return slides;
}

/**
 * §18.2.4 The evidence slides that are `id`'s children **right now** — recomputed from
 * adjacency at the moment of the action, never recorded on either slide, so archiving and
 * restoring into a changed deck re-derives rather than resurrecting a stale parent.
 *
 * Empty unless `id` is an active storyline slide.
 */
export function evidenceChildIds(slides: SlideData[], id: string): string[] {
  const active = slides.filter((s) => !s.archived);
  const at = active.findIndex((s) => s.id === id);
  if (at === -1 || depthOf(active[at]) !== DEPTH_STORYLINE) return [];
  const ids: string[] = [];
  for (let i = at + 1; i < active.length; i++) {
    if (depthOf(active[i]) !== DEPTH_EVIDENCE) break;
    ids.push(active[i].id);
  }
  return ids;
}

/**
 * §18.2.4 The mirror of `evidenceChildIds` for Restore: the archived evidence slides sitting
 * directly behind an archived storyline slide in the full array. Archiving a group archives a
 * contiguous run, so restoring reads that run back — and stops at the first slide that is not
 * part of it, so a slide archived separately in between is not dragged along.
 */
export function archivedEvidenceAfter(
  slides: SlideData[],
  id: string,
): string[] {
  const at = slides.findIndex((s) => s.id === id);
  if (at === -1 || depthOf(slides[at]) !== DEPTH_STORYLINE) return [];
  const ids: string[] = [];
  for (let i = at + 1; i < slides.length; i++) {
    if (!slides[i].archived || depthOf(slides[i]) !== DEPTH_EVIDENCE) break;
    ids.push(slides[i].id);
  }
  return ids;
}

// === The outline ===

export type PyramidRow =
  | { kind: "section"; id: string; label: string }
  | {
      kind: "slide";
      id: string;
      slide: SlideData;
      // Index in the ACTIVE array the caller passed — what every other per-slide prop
      // (thumbnails, snapshots, the centre column's `index`) is already keyed by.
      index: number;
      // §18.2.9 Already numbered: `4`, `4.2`, `B7`, or `null` on a cover.
      number: string | null;
      level: SlideLevel;
      // §18.2.5 Already indented: the depth the row renders at.
      indent: 0 | 1 | 2;
      canMoveUp: boolean;
      canMoveDown: boolean;
      // §18.2.12 This storyline slide carries more evidence than the principle advises.
      crowded: boolean;
    };

export interface PyramidOutline {
  rows: PyramidRow[];
  storylineCount: number;
  backupCount: number;
  // §18.2.12 The deck-level half of the nudge.
  storylineCrowded: boolean;
}

/**
 * §18.2.5 The one selector behind both slide lists and the centre column: rows already
 * numbered, already indented, already knowing whether their move buttons do anything, with a
 * **Backups** section row in front of the suffix. `active` must be the active slides in order
 * and `numbers` the matching `slideNumbering(active)` — passed in rather than computed here so
 * the deck has exactly one numbering pass and this file does not import the numbering helper
 * that imports it.
 */
export function groupSlides(
  active: SlideData[],
  numbers: (string | null)[],
): PyramidOutline {
  // One pass to place every slide: `gi` = its group, `ei` = its position within that group's
  // evidence (-1 for the head), `bi` = its position in the backup suffix.
  const gi: number[] = [];
  const ei: number[] = [];
  const bi: number[] = [];
  const evidenceCount: number[] = [];
  let g = -1;
  let b = -1;
  for (const slide of active) {
    const d = depthOf(slide);
    if (d === DEPTH_BACKUP) {
      gi.push(-1);
      ei.push(-1);
      bi.push(++b);
      continue;
    }
    if (d === DEPTH_EVIDENCE && g >= 0) {
      gi.push(g);
      ei.push(evidenceCount[g]++);
      bi.push(-1);
      continue;
    }
    g++;
    evidenceCount[g] = 0;
    gi.push(g);
    ei.push(-1);
    bi.push(-1);
  }
  const storylineCount = g + 1;
  const backupCount = b + 1;

  const rows: PyramidRow[] = [];
  active.forEach((slide, index) => {
    if (bi[index] === 0) {
      rows.push({ kind: "section", id: "backups", label: "Backups" });
    }
    const level = levelOf(slide);
    const isBackup = depth(level) === DEPTH_BACKUP;
    const isEvidence = ei[index] !== -1;
    rows.push({
      kind: "slide",
      id: slide.id,
      slide,
      index,
      number: numbers[index] ?? null,
      level,
      indent: isEvidence ? 1 : 0,
      canMoveUp: isBackup
        ? bi[index] > 0
        : isEvidence
          ? !(gi[index] === 0 && ei[index] === 0)
          : gi[index] > 0,
      canMoveDown: isBackup
        ? bi[index] < backupCount - 1
        : isEvidence
          ? !(
              gi[index] === storylineCount - 1 &&
              ei[index] === evidenceCount[gi[index]] - 1
            )
          : gi[index] < storylineCount - 1,
      crowded:
        !isBackup &&
        !isEvidence &&
        evidenceCount[gi[index]] > EVIDENCE_SOFT_MAX,
    });
  });

  return {
    rows,
    storylineCount,
    backupCount,
    storylineCrowded: storylineCount > STORYLINE_SOFT_MAX,
  };
}

// === Presentation traversal (§18.3.1/§18.3.2, the simple version) ===
//
// Two axes, and that is the whole model: **←/→ move along the storyline, ↑/↓ move between a
// storyline slide and its evidence.** Read the deck as a grid — one column per storyline
// slide, one row per level — and the four keys are the four directions in it. Changing column
// always lands on that column's storyline slide, so the argument is what you step through and
// the evidence is a detour you take deliberately and come back from.
//
// **Backups are flat**, as the backup section already is in every other path: ← and → step
// through them linearly, ↑ and ↓ do nothing. → from the last storyline slide enters the
// suffix and ← from the first backup returns to the deck, so the appendix is reachable and
// escapable without a second concept.
//
// **A flat deck presents exactly as it did before this existed**: with no evidence and no
// backups every slide is its own column, so → is "the next slide" and ← is "the previous
// one", byte for byte the old behaviour. That is why there is no Pyramid/Linear mode switch
// (§18.3.4) — a deck with one level cannot tell the difference.
//
// Where this deliberately differs from §18.3.1 as written: there, → from an evidence slide
// walks to the next evidence SIBLING and only then to the next storyline slide. Here it
// leaves for the next storyline slide immediately, and ↓ is what walks the evidence column.
// One key per axis, no key that means two things depending on where you are standing — which
// is the property that makes it explainable to a presenter in one sentence.

export type PyramidDirection = "next" | "prev" | "down" | "up";

// Where a slide sits in the grid: a group column plus a row (`-1` = the storyline slide
// itself), or a position in the flat backup suffix.
type Spot =
  | { kind: "group"; gi: number; ei: number }
  | { kind: "backup"; bi: number }
  | null;

function locate(groups: Group[], backups: SlideData[], id: string): Spot {
  for (let gi = 0; gi < groups.length; gi++) {
    if (groups[gi].head.id === id) return { kind: "group", gi, ei: -1 };
    const ei = groups[gi].evidence.findIndex((s) => s.id === id);
    if (ei !== -1) return { kind: "group", gi, ei };
  }
  const bi = backups.findIndex((s) => s.id === id);
  return bi === -1 ? null : { kind: "backup", bi };
}

/**
 * The id to step to, or `null` when the move runs off the end of its axis — which the caller
 * treats as "stay put", the way the old index clamp did at slide 0 and the last slide.
 *
 * `active` must be the active slides in order (presentation already holds exactly that).
 */
export function pyramidStep(
  active: SlideData[],
  fromId: string,
  direction: PyramidDirection,
): string | null {
  const { groups, backups } = buildGroups(active);
  const at = locate(groups, backups, fromId);
  if (!at) return null;

  if (at.kind === "backup") {
    if (direction === "next") return backups[at.bi + 1]?.id ?? null;
    // ← off the front of the suffix returns to the deck it sits behind, mirroring the → that
    // got you in. Without it the appendix is a one-way door.
    if (direction === "prev")
      return at.bi > 0
        ? backups[at.bi - 1].id
        : (groups[groups.length - 1]?.head.id ?? null);
    return null;
  }

  const { head, evidence } = groups[at.gi];
  switch (direction) {
    case "next":
      return at.gi < groups.length - 1
        ? groups[at.gi + 1].head.id
        : (backups[0]?.id ?? null);
    case "prev":
      return at.gi > 0 ? groups[at.gi - 1].head.id : null;
    case "down":
      return (at.ei === -1 ? evidence[0] : evidence[at.ei + 1])?.id ?? null;
    case "up":
      if (at.ei === -1) return null;
      return at.ei > 0 ? evidence[at.ei - 1].id : head.id;
  }
}

/**
 * How many evidence slides sit below `fromId` in its own group — all of them from the
 * storyline slide, the remainder from an evidence slide, zero anywhere else.
 *
 * §18.3.6, at its smallest: the down axis is invisible without something on screen saying it
 * is there, and a presenter who does not know a slide has evidence will never press ↓.
 */
export function evidenceBelow(active: SlideData[], fromId: string): number {
  const { groups, backups } = buildGroups(active);
  const at = locate(groups, backups, fromId);
  if (!at || at.kind === "backup") return 0;
  return groups[at.gi].evidence.length - (at.ei + 1);
}
